import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, History } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import useAuthStore from '../../lib/auth-store';
import { attrFetch, apiFetch, isAdmin, roleLabel } from './attributionApi';
import { RelationshipRoster } from './RelationshipRoster';
import { AttributionTree } from './AttributionTree';
import { AddRelationshipDialog, ReasonDialog, SetAttributionDialog } from './AttributionDialogs';

function Section({ title, hint, action, children, testId }) {
  return (
    <section className="space-y-3" data-testid={testId}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
          <p className="text-xs text-gray-500">{hint}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function useAttributionData(groupId, token, history) {
  const [state, setState] = useState({ rels: [], tree: null, roles: {}, parties: [], partners: [] });
  const load = useCallback(async () => {
    try {
      const [rels, resolved, roles, parties, partners] = await Promise.all([
        attrFetch(token, `/groups/${groupId}/relationships?include_history=${history}`),
        attrFetch(token, `/groups/${groupId}/attribution/resolved`),
        attrFetch(token, '/relationship-roles'),
        attrFetch(token, '/relationship-parties'),
        apiFetch(token, '/affiliates/directory').catch(() => []),
      ]);
      setState({ rels, tree: resolved.tree, roles, parties, partners });
    } catch (e) { toast.error(e.message); }
  }, [groupId, token, history]);
  useEffect(() => { load(); }, [load]);
  return [state, load];
}

export default function RelationshipsAttributionTab({ groupId }) {
  const { token, user } = useAuthStore();
  const admin = isAdmin(user?.role);
  const [history, setHistory] = useState(false);
  const [{ rels, tree, roles, parties, partners }, reload] = useAttributionData(groupId, token, history);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (path, body, msg) => {
    setBusy(true);
    try {
      await attrFetch(token, path, 'POST', body);
      toast.success(msg);
      setDialog(null);
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const canEdit = (r) => admin || !(roles.protected || []).includes(r.role);
  const day = (d) => (d ? new Date(d).toISOString() : undefined);

  return (
    <div className="space-y-8" data-testid="relationships-attribution-tab">
      <Section testId="relationships-section" title="Merchant relationships"
               hint="Who works this merchant. Separate from your org chart — a referrer never joins anyone's downline."
               action={
                 <div className="flex items-center gap-3">
                   <label className="flex items-center gap-2 text-xs text-gray-500">
                     <History className="w-3.5 h-3.5" /> History
                     <Switch checked={history} onCheckedChange={setHistory} data-testid="relationships-history-toggle" />
                   </label>
                   <Button size="sm" onClick={() => setDialog({ type: 'add' })} data-testid="add-relationship-btn"><Plus className="w-4 h-4 mr-1" /> Assign</Button>
                 </div>
               }>
        <RelationshipRoster relationships={rels} canEdit={canEdit}
                            onChange={(r) => setDialog({ type: 'change', r })} onEnd={(r) => setDialog({ type: 'end', r })} />
      </Section>

      <Section testId="attribution-section" title="Attribution"
               hint="Who originated each level. The most specific record wins; everything below inherits until overridden.">
        <AttributionTree tree={tree} admin={admin}
                         onSet={(node) => setDialog({ type: 'set', node })} onTransfer={(node) => setDialog({ type: 'transfer', node })} />
      </Section>

      {dialog?.type === 'add' && (
        <AddRelationshipDialog open onClose={() => setDialog(null)} roles={roles} parties={parties} partners={partners} busy={busy}
                               onSave={(b) => save(`/groups/${groupId}/relationships`, { ...b, effective_from: day(b.effective_from) }, 'Relationship assigned')} />
      )}
      {dialog?.type === 'change' && (
        <ReasonDialog open testId="change-relationship-dialog" title={`Change ${roleLabel(dialog.r.role)}`} needsParty dateLabel="Effective from"
                      allowExternal={(roles.external_allowed || []).includes(dialog.r.role)}
                      parties={parties} busy={busy} onClose={() => setDialog(null)}
                      onSave={(f) => save(`/relationships/${dialog.r.id}/change`, {
                        user_id: f.external ? undefined : f.user_id || undefined,
                        external_party: f.external ? { name: f.external } : undefined,
                        reason: f.reason, effective_from: day(f.date),
                      }, 'Relationship changed — previous holder kept in history')} />
      )}
      {dialog?.type === 'end' && (
        <ReasonDialog open testId="end-relationship-dialog" title={`End ${roleLabel(dialog.r.role)}`} dateLabel="Effective to"
                      parties={parties} busy={busy} onClose={() => setDialog(null)}
                      onSave={(f) => save(`/relationships/${dialog.r.id}/end`, { reason: f.reason, effective_to: day(f.date) }, 'Relationship ended')} />
      )}
      {dialog?.type === 'set' && (
        <SetAttributionDialog open parties={parties} busy={busy} onClose={() => setDialog(null)}
                              target={{ ...dialog.node, inheritedFrom: dialog.node.originating_user?.name }}
                              onSave={(f) => save(`/groups/${groupId}/attribution`, { ...f, scope: dialog.node.scope, scope_id: dialog.node.scope_id }, 'Attribution saved')} />
      )}
      {dialog?.type === 'transfer' && (
        <ReasonDialog open kinds testId="transfer-attribution-dialog" title={`Change attribution · ${dialog.node.label}`} needsParty dateLabel="Effective from"
                      parties={parties} busy={busy} onClose={() => setDialog(null)}
                      onSave={(f) => save(`/attribution/${dialog.node.attribution_id}/transfer`, { originating_user_id: f.user_id, reason: f.reason, kind: f.kind, effective_from: day(f.date) }, 'Attribution changed — original record retained')} />
      )}
    </div>
  );
}
